// Lead capture form — reusable sales form for demo booking and lead generation
"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, Check, Loader2 } from "lucide-react";
import { contactPost } from "@/tools/calls";
import type { SelectOption } from "@/data/page-data/book-a-demo";
import { executeRecaptcha } from "@/lib/recaptcha";
import {
  FORM_SECURITY_LIMITS,
  exceedsPayloadLimit,
  isSubmissionTooFast,
} from "@/lib/formSecurity";

// ─── Types ────────────────────────────────────────────────────────────────────

interface LeadCaptureFormProps {
  formName: string;
  title: string;
  intro: string;
  consentText: string;
  submitButton: string;
  successHeadline: string;
  successBody: string;
  portfolioSizeOptions: SelectOption[];
  propertyTypeOptions: SelectOption[];
  helpNeededOptions: SelectOption[];
  currentSetupOptions: SelectOption[];
  biggestChallengeOptions: SelectOption[];
  moveTimelineOptions: SelectOption[];
  className?: string;
  compact?: boolean;
}

interface FormState {
  first_name: string;
  last_name: string;
  work_email: string;
  phone_number: string;
  company_name: string;
  job_title: string;
  website: string;
  portfolio_size: string;
  property_type: string;
  main_operating_locations: string;
  help_needed: string;
  current_setup: string;
  biggest_challenge: string;
  move_timeline: string;
  additional_notes: string;
}

function getOptionLabel(options: SelectOption[], value: string) {
  if (!value) return "Not provided";
  return options.find((option) => option.value === value)?.label ?? value;
}

function formatValue(value: string) {
  return value.trim() || "Not provided";
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function toHtmlMultiline(value: string) {
  return escapeHtml(value).replace(/\n/g, "<br />");
}

function formatSubmittedAtUtc(isoDate: string) {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return isoDate;

  const formatted = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }).format(parsed);

  return `${formatted} UTC`;
}

function normaliseWebsiteUrl(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function validate(data: FormState): Partial<Record<keyof FormState, string>> {
  const errs: Partial<Record<keyof FormState, string>> = {};
  if (!data.first_name.trim()) errs.first_name = "Please enter your first name.";
  if (!data.last_name.trim()) errs.last_name = "Please enter your last name.";
  if (!data.work_email.trim()) {
    errs.work_email = "Please enter your work email address.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.work_email.trim())) {
    errs.work_email = "Please enter a valid email address.";
  }
  if (!data.company_name.trim()) errs.company_name = "Please enter your company name.";
  if (!data.portfolio_size) errs.portfolio_size = "Please select your portfolio size.";
  if (!data.property_type) errs.property_type = "Please select a property type.";
  if (!data.help_needed) errs.help_needed = "Please let us know what you need help with.";
  return errs;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const inputClasses =
  "w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/40 focus:border-purple-400 focus:bg-white transition-all placeholder:text-gray-400";

const labelClasses = "block text-sm font-medium text-gray-700 mb-1.5";

const inputErrorClasses =
  "w-full px-4 py-3 bg-red-50 border border-red-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-400/40 focus:border-red-400 focus:bg-white transition-all placeholder:text-gray-400";

function fieldClass(hasError: boolean) {
  return hasError ? inputErrorClasses : inputClasses;
}

const requiredMark = <span className="text-purple-500 ml-0.5">*</span>;

const optionalMark = (
  <span className="text-gray-400 font-normal ml-1">(optional)</span>
);

// ─── Component ────────────────────────────────────────────────────────────────

export function LeadCaptureForm({
  formName,
  title,
  intro,
  consentText,
  submitButton,
  successHeadline,
  successBody,
  portfolioSizeOptions,
  propertyTypeOptions,
  helpNeededOptions,
  currentSetupOptions,
  biggestChallengeOptions,
  moveTimelineOptions,
  className = "",
  compact = false,
}: LeadCaptureFormProps) {
  const [formData, setFormData] = useState<FormState>({
    first_name: "",
    last_name: "",
    work_email: "",
    phone_number: "",
    company_name: "",
    job_title: "",
    website: "",
    portfolio_size: "",
    property_type: "",
    main_operating_locations: "",
    help_needed: "",
    current_setup: "",
    biggest_challenge: "",
    move_timeline: "",
    additional_notes: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");
  const [startedAt, setStartedAt] = useState(() => Date.now());
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  // Hidden CRM fields
  const [hiddenFields, setHiddenFields] = useState({
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_term: "",
    utm_content: "",
    referrer_url: "",
    landing_page_url: "",
    form_name: formName,
    lead_source: "website",
    page_variant: "book-a-demo",
    timestamp: "",
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    setHiddenFields((prev) => ({
      ...prev,
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      utm_term: params.get("utm_term") || "",
      utm_content: params.get("utm_content") || "",
      referrer_url: document.referrer || "",
      landing_page_url: window.location.href,
    }));
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name as keyof FormState]) return prev;
      const next = { ...prev };
      delete next[name as keyof FormState];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setIsSubmitting(true);

    try {
      if (honeypot.trim()) {
        setSubmitted(true);
        return;
      }

      if (isSubmissionTooFast(startedAt, FORM_SECURITY_LIMITS.minSubmissionMs)) {
        throw new Error("Please wait a moment before submitting.");
      }

      if (formData.additional_notes.length > FORM_SECURITY_LIMITS.maxNotesChars) {
        throw new Error("Additional notes are too long. Please shorten your message.");
      }

      const submittedAt = new Date().toISOString();
      const submittedAtDisplay = formatSubmittedAtUtc(submittedAt);
      const helpNeededLabel = getOptionLabel(helpNeededOptions, formData.help_needed);
      const portfolioSizeLabel = getOptionLabel(portfolioSizeOptions, formData.portfolio_size);
      const propertyTypeLabel = getOptionLabel(propertyTypeOptions, formData.property_type);
      const currentSetupLabel = getOptionLabel(currentSetupOptions, formData.current_setup);
      const biggestChallengeLabel = getOptionLabel(biggestChallengeOptions, formData.biggest_challenge);
      const moveTimelineLabel = getOptionLabel(moveTimelineOptions, formData.move_timeline);

      const fullName = `${formData.first_name} ${formData.last_name}`.trim();
      const safeEmail = formData.work_email.trim();
      const safePhone = formData.phone_number.trim();
      const websiteUrl = normaliseWebsiteUrl(formData.website);

      const captchaToken = await executeRecaptcha("book_a_demo_submit");

      const message = `
<div>
  <h2><u>Booking Callback Request</u></h2>
  <p><strong>Action required:</strong> Please call this lead back as soon as possible.</p>
  <p><strong>Client message:</strong><br />${toHtmlMultiline(formatValue(formData.additional_notes))}</p>
  <hr />
  <h3><u>Primary Contact</u></h3>
  <p>
    <strong>${escapeHtml(formatValue(fullName))}</strong><br />
    Phone: ${safePhone ? `<a href="tel:${escapeHtml(safePhone)}">${escapeHtml(safePhone)}</a>` : "Not provided"}<br />
    Email: <a href="mailto:${escapeHtml(safeEmail)}">${escapeHtml(safeEmail)}</a><br />
    Company: ${escapeHtml(formatValue(formData.company_name))}<br />
    Job title: ${escapeHtml(formatValue(formData.job_title))}<br />
    Website: ${websiteUrl ? `<a href="${escapeHtml(websiteUrl)}">${escapeHtml(formData.website.trim())}</a>` : "Not provided"}
  </p>
  <h3><u>Booking Details</u></h3>
  <ul>
    <li><strong>Request type:</strong> Booking callback request</li>
    <li><strong>Help needed:</strong> ${escapeHtml(helpNeededLabel)}</li>
    <li><strong>Portfolio size:</strong> ${escapeHtml(portfolioSizeLabel)}</li>
    <li><strong>Property type:</strong> ${escapeHtml(propertyTypeLabel)}</li>
    <li><strong>Main operating locations:</strong> ${escapeHtml(formatValue(formData.main_operating_locations))}</li>
    <li><strong>Current setup:</strong> ${escapeHtml(currentSetupLabel)}</li>
    <li><strong>Biggest challenge:</strong> ${escapeHtml(biggestChallengeLabel)}</li>
    <li><strong>Move timeline:</strong> ${escapeHtml(moveTimelineLabel)}</li>
    <li><strong>Source page:</strong> Book a Demo</li>
    <li><strong>Form name:</strong> ${escapeHtml(formName)}</li>
  </ul>
</div>`.trim();

      if (exceedsPayloadLimit(message, FORM_SECURITY_LIMITS.maxPayloadBytes)) {
        throw new Error("Your request is too large. Please shorten your notes and try again.");
      }

      // Show success before the API call — cold-start retries happen in the background.
      // The Promise chain survives Next.js client-side navigation because JS keeps running
      // after a component unmounts. keepalive:true on each fetch also covers tab-close
      // for any request already in flight
      setSubmitted(true);

      contactPost({
        email: formData.work_email.trim(),
        date_submitted: submittedAt,
        contacted: 0,
        submission_type: "contact",
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        company: formData.company_name.trim() || undefined,
        phone: formData.phone_number.trim() || undefined,
        portfolio_size: formData.portfolio_size || undefined,
        inquiry_type: helpNeededLabel,
        message,
        page: "contact",
        captcha_token: captchaToken,
        captcha_provider: "recaptcha-v3",
        form_context: "book-a-demo",
        submission_duration_ms: Date.now() - startedAt,
      }).catch((err: unknown) => {
        console.error("[book-a-demo] Submission failed after all retries:", err);
      });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Could not submit your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setSubmitError(null);
    setFormData({
      first_name: "",
      last_name: "",
      work_email: "",
      phone_number: "",
      company_name: "",
      job_title: "",
      website: "",
      portfolio_size: "",
      property_type: "",
      main_operating_locations: "",
      help_needed: "",
      current_setup: "",
      biggest_challenge: "",
      move_timeline: "",
      additional_notes: "",
    });
    setHoneypot("");
    setStartedAt(Date.now());
    setErrors({});
  };

  // ── Success state ─────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <div id="lead-form" className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-8 sm:p-10 ${className}`}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-8"
        >
          <div className="w-16 h-16 rounded-full bg-purple-500 flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">{successHeadline}</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">{successBody}</p>
          <button onClick={resetForm} className="text-purple-500 font-medium hover:text-purple-600 transition-colors">
            Submit another request
          </button>
        </motion.div>
      </div>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────────

  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm ${compact ? "p-5 sm:p-6" : "p-6 sm:p-8"} ${className}`}>
      {/* Header */}
      <div className="mb-6">
        <h3 className={`font-bold text-gray-900 mb-1.5 ${compact ? "text-lg" : "text-xl sm:text-2xl"}`}>
          {title}
        </h3>
        <p className="text-sm text-gray-500 leading-relaxed">{intro}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {/* Name row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="first_name" className={labelClasses}>
              First name {requiredMark}
            </label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
              maxLength={FORM_SECURITY_LIMITS.maxNameChars}
              className={fieldClass(!!errors.first_name)}
              placeholder="Jane"
            />
            {errors.first_name && (
              <p className="mt-1.5 text-xs text-red-600">{errors.first_name}</p>
            )}
          </div>
          <div>
            <label htmlFor="last_name" className={labelClasses}>
              Last name {requiredMark}
            </label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
              maxLength={FORM_SECURITY_LIMITS.maxNameChars}
              className={fieldClass(!!errors.last_name)}
              placeholder="Smith"
            />
            {errors.last_name && (
              <p className="mt-1.5 text-xs text-red-600">{errors.last_name}</p>
            )}
          </div>
        </div>

        {/* Email + Phone */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="work_email" className={labelClasses}>
              Work email {requiredMark}
            </label>
            <input
              type="email"
              id="work_email"
              name="work_email"
              value={formData.work_email}
              onChange={handleChange}
              required
              maxLength={FORM_SECURITY_LIMITS.maxEmailChars}
              className={fieldClass(!!errors.work_email)}
              placeholder="jane@company.com"
            />
            {errors.work_email && (
              <p className="mt-1.5 text-xs text-red-600">{errors.work_email}</p>
            )}
          </div>
          <div>
            <label htmlFor="phone_number" className={labelClasses}>
              Phone number {optionalMark}
            </label>
            <input
              type="tel"
              id="phone_number"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              maxLength={FORM_SECURITY_LIMITS.maxPhoneChars}
              className={inputClasses}
              placeholder="+44 7700 900000"
            />
          </div>
        </div>

        {/* Company + Job Title */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="company_name" className={labelClasses}>
              Company name {requiredMark}
            </label>
            <input
              type="text"
              id="company_name"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              required
              maxLength={FORM_SECURITY_LIMITS.maxCompanyChars}
              className={fieldClass(!!errors.company_name)}
              placeholder="Acme Properties"
            />
            {errors.company_name && (
              <p className="mt-1.5 text-xs text-red-600">{errors.company_name}</p>
            )}
          </div>
          <div>
            <label htmlFor="job_title" className={labelClasses}>
              Job title {optionalMark}
            </label>
            <input
              type="text"
              id="job_title"
              name="job_title"
              value={formData.job_title}
              onChange={handleChange}
              maxLength={FORM_SECURITY_LIMITS.maxCompanyChars}
              className={inputClasses}
              placeholder="Operations Manager"
            />
          </div>
        </div>

        {/* Website */}
        <div>
          <label htmlFor="website" className={labelClasses}>
            Website {optionalMark}
          </label>
          <input
            type="url"
            id="website"
            name="website"
            value={formData.website}
            onChange={handleChange}
            maxLength={FORM_SECURITY_LIMITS.maxWebsiteChars}
            className={inputClasses}
            placeholder="https://yourcompany.com"
          />
        </div>

        {/* Portfolio size + Property type */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="portfolio_size" className={labelClasses}>
              Portfolio size {requiredMark}
            </label>
            <select
              id="portfolio_size"
              name="portfolio_size"
              value={formData.portfolio_size}
              onChange={handleChange}
              required
              className={fieldClass(!!errors.portfolio_size)}
            >
              <option value="">Select&hellip;</option>
              {portfolioSizeOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {errors.portfolio_size && (
              <p className="mt-1.5 text-xs text-red-600">{errors.portfolio_size}</p>
            )}
          </div>
          <div>
            <label htmlFor="property_type" className={labelClasses}>
              Property type {requiredMark}
            </label>
            <select
              id="property_type"
              name="property_type"
              value={formData.property_type}
              onChange={handleChange}
              required
              className={fieldClass(!!errors.property_type)}
            >
              <option value="">Select&hellip;</option>
              {propertyTypeOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {errors.property_type && (
              <p className="mt-1.5 text-xs text-red-600">{errors.property_type}</p>
            )}
          </div>
        </div>

        {/* Operating locations */}
        <div>
          <label htmlFor="main_operating_locations" className={labelClasses}>
            Main operating locations {optionalMark}
          </label>
          <input
            type="text"
            id="main_operating_locations"
            name="main_operating_locations"
            value={formData.main_operating_locations}
            onChange={handleChange}
            maxLength={FORM_SECURITY_LIMITS.maxLocationChars}
            className={inputClasses}
            placeholder="e.g. London, Manchester, Edinburgh"
          />
        </div>

        {/* Help needed + Current setup */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="help_needed" className={labelClasses}>
              What do you need help with? {requiredMark}
            </label>
            <select
              id="help_needed"
              name="help_needed"
              value={formData.help_needed}
              onChange={handleChange}
              required
              className={fieldClass(!!errors.help_needed)}
            >
              <option value="">Select&hellip;</option>
              {helpNeededOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {errors.help_needed && (
              <p className="mt-1.5 text-xs text-red-600">{errors.help_needed}</p>
            )}
          </div>
          <div>
            <label htmlFor="current_setup" className={labelClasses}>
              Current setup {optionalMark}
            </label>
            <select
              id="current_setup"
              name="current_setup"
              value={formData.current_setup}
              onChange={handleChange}
              className={inputClasses}
            >
              <option value="">Select&hellip;</option>
              {currentSetupOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Biggest challenge + Move timeline */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="biggest_challenge" className={labelClasses}>
              Biggest challenge {optionalMark}
            </label>
            <select
              id="biggest_challenge"
              name="biggest_challenge"
              value={formData.biggest_challenge}
              onChange={handleChange}
              className={inputClasses}
            >
              <option value="">Select&hellip;</option>
              {biggestChallengeOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="move_timeline" className={labelClasses}>
              When are you looking to move? {optionalMark}
            </label>
            <select
              id="move_timeline"
              name="move_timeline"
              value={formData.move_timeline}
              onChange={handleChange}
              className={inputClasses}
            >
              <option value="">Select&hellip;</option>
              {moveTimelineOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Additional notes */}
        <div>
          <label htmlFor="additional_notes" className={labelClasses}>
            Anything else we should know? {optionalMark}
          </label>
          <textarea
            id="additional_notes"
            name="additional_notes"
            value={formData.additional_notes}
            onChange={handleChange}
            rows={3}
            maxLength={FORM_SECURITY_LIMITS.maxNotesChars}
            className={inputClasses}
            placeholder="Tell us more about your operation or what you are looking for..."
          />
        </div>

        <div className="hidden" aria-hidden="true">
          <label htmlFor="website_url">Website URL</label>
          <input
            id="website_url"
            name="website_url"
            type="text"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            autoComplete="off"
            tabIndex={-1}
          />
        </div>

        {/* Error */}
        {submitError && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {submitError}
          </div>
        )}

        {/* Consent */}
        <p className="text-xs text-gray-400 leading-relaxed">{consentText}</p>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-white font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
          style={{ backgroundColor: "#8c52ff" }}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Submitting&hellip;
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              {submitButton}
            </>
          )}
        </button>

        {/* Hidden CRM fields */}
        {Object.entries(hiddenFields).map(([key, val]) => (
          <input key={key} type="hidden" name={key} value={val} />
        ))}
      </form>
    </div>
  );
}
